import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

import {
  ACCESS_CONTROL_KEY,
  useItemUsers,
} from '../../../../hooks/useAccessControl';
import {
  AccessControlItem,
  AccessControlKind,
  AccessControlUser,
  accessControlError,
  accessControlService,
} from '../../../../services/accessControlService';
import { KIND_LABEL, diffIds, plural } from '../../../../utils/accessControl';
import { apllyToast } from '../../../Global/Toast2.0';
import {
  CopySource,
  ItemUsersEditor,
} from '../../AccessControl/ItemUsersEditor';
import { ModalComponent } from '..';

interface Props {
  kind: AccessControlKind;
  item?: AccessControlItem;
  isOpen: boolean;
  onClose: () => void;
  users: AccessControlUser[];
  usersLoading: boolean;
  copySources: CopySource[];
}

// Quem tem a permissão (ou função): adiciona, remove e copia de outra. Tudo
// é gravado de uma vez ao salvar.
export function ModalItemUsers({
  kind,
  item,
  isOpen,
  onClose,
  users,
  usersLoading,
  copySources,
}: Props) {
  const queryClient = useQueryClient();
  const label = KIND_LABEL[kind];
  const current = useItemUsers(kind, isOpen ? item?.id : undefined);
  const initialIds = useMemo(
    () => (current.data || []).map(user => user.id),
    [current.data],
  );
  const [value, setValue] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && current.data) {
      setValue(current.data.map(user => user.id));
    }
  }, [isOpen, current.data]);

  const { added, removed } = diffIds(initialIds, value);
  const changed = added.length > 0 || removed.length > 0;
  const ready = !!current.data && !current.isFetching;

  const handleSave = async () => {
    if (!item) {
      return;
    }

    setSaving(true);
    try {
      const result = await accessControlService.syncItemUsers(
        kind,
        item.id,
        value,
      );

      await queryClient.invalidateQueries(ACCESS_CONTROL_KEY);
      apllyToast(
        'success',
        `Usuários atualizados: ${result.added} adicionado(s), ${result.removed} removido(s).`,
      );
      onClose();
    } catch (err) {
      apllyToast(
        'error',
        accessControlError(err, 'Não foi possível salvar os usuários.'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalComponent
      title={item ? `Usuários: ${item.name}` : 'Usuários'}
      isOpen={isOpen}
      onClose={onClose}
      sizeSpecifyc="2xl"
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <Box display="flex" flexDirection="column" flex="1" minH="0">
        <ModalBody>
          <Stack spacing="4">
            {item && (
              <Text fontSize="sm" color="gray.400">
                Código <b>{item.identification}</b>
                {item.bi_reports > 0 &&
                  ` · libera ${plural(
                    item.bi_reports,
                    'relatório',
                    'relatórios',
                  )} de BI`}
                . Quem receber a {label.one} precisa atualizar a página do
                SAMASC para ver o acesso.
              </Text>
            )}

            {current.isError && (
              <Alert status="error" color="red.900" borderRadius="md">
                <AlertIcon />
                <AlertDescription flex="1">
                  Não foi possível carregar os usuários.
                </AlertDescription>
                <Button size="sm" onClick={() => current.refetch()}>
                  Tentar novamente
                </Button>
              </Alert>
            )}

            {!current.isError && (
              <ItemUsersEditor
                users={users}
                isLoading={usersLoading || !ready}
                initialIds={initialIds}
                value={value}
                onChange={setValue}
                copySources={copySources.filter(
                  source =>
                    !(source.kind === kind && source.item.id === item?.id),
                )}
              />
            )}

            {removed.length > 0 && (
              <Alert status="warning" color="orange.900" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {plural(removed.length, 'usuário vai', 'usuários vão')} perder
                  a {label.one} ao salvar.
                </AlertDescription>
              </Alert>
            )}
          </Stack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor="gray.600">
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={!ready || !changed}
          >
            Salvar
          </Button>
        </ModalFooter>
      </Box>
    </ModalComponent>
  );
}
